import { useForm } from 'react-hook-form';
import React, { useCallback, useContext, useMemo } from 'react';
import { showToast } from '../../../../helpers/showToast';
import { ApolloError, useApolloClient } from '@apollo/client';
import { getApolloErrorMessage, remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { FormModal } from '../../../../components/containers/FormModal';
import { Input } from '../../../../components/forms/Input';
import { TreeMenuContext } from '../TreeMenu/TreeMenuProvider';
import { FormOptions } from '../../../../constants/form';
import { ValidationMessages } from 'constants/validation-messages';
import { PlanUsageType, UsageFieldsFragmentDoc } from 'models/graphql';
import { AppContext } from 'providers/AppProvider';

export type AddEditSourceModalProps = {
  createSourceHandler: CallableFunction;
  updateSourceHandler: CallableFunction;
  closeModalHandler: CallableFunction;
};

export const AddEditSourceModalView = (props: AddEditSourceModalProps): JSX.Element => {
  const { updateSourceHandler, createSourceHandler, closeModalHandler } = props;
  const { selectedMenuItem } = useContext(TreeMenuContext);
  const { setLimitReachedModalState } = useContext(AppContext);
  const { cache } = useApolloClient();
  const formInstance = useForm({
    ...FormOptions
  });
  const componentVerbs = useMemo(
    () =>
      selectedMenuItem
        ? { title: 'Rename', error: 'updating', label: 'Current' }
        : { title: 'Add', error: 'creating', label: `Enter` },
    [selectedMenuItem]
  );

  const addSource = useCallback(() => {
    createSourceHandler(formInstance.getValues('treeModalAddEditSource'))
      .then(() => {
        const oldUsage: PlanUsageType | null = cache.readFragment({
          fragment: UsageFieldsFragmentDoc,
          fragmentName: 'UsageFields',
          id: 'PlanUsageType:{}'
        });
        const newUsage = { ...oldUsage, importSources: (oldUsage?.importSources ?? 0) + 1 };
        cache.writeFragment({
          fragment: UsageFieldsFragmentDoc,
          fragmentName: 'UsageFields',
          id: 'PlanUsageType:{}',
          data: newUsage
        });
      })
      .catch((error: ApolloError) => {
        const mappedError = remapApolloErrors(error);
        if (getApolloErrorMessage(error) === 'PLAN_LIMIT_EXCEEDED') {
          setLimitReachedModalState({
            isOpen: true,
            type: 'importSource'
          });
        } else {
          showToast({
            summary: `Error ${componentVerbs.error} the source`,
            detail: mappedError,
            severity: 'error'
          });
        }
      })
      .finally(() => {
        closeModalHandler();
      });
  }, [closeModalHandler, componentVerbs, createSourceHandler, formInstance, cache, setLimitReachedModalState]);

  const updateSource = useCallback(() => {
    updateSourceHandler(selectedMenuItem?.id, formInstance.getValues('treeModalAddEditSource'))
      .catch((error: ApolloError) => {
        const mappedError = remapApolloErrors(error);
        showToast({
          summary: `Error ${componentVerbs.error} the source`,
          detail: mappedError,
          severity: 'error'
        });
      })
      .finally(() => {
        closeModalHandler();
      });
  }, [closeModalHandler, componentVerbs, formInstance, selectedMenuItem, updateSourceHandler]);

  const isValid = formInstance.formState.isValid;
  const modalProps = useMemo(
    () => ({
      onClose: closeModalHandler,
      showCloseIcon: true,
      formInstance: formInstance,
      loading: false,
      onSubmit: () => {
        const trimmedName = formInstance.getValues('treeModalAddEditSource').trim().replace(/\s+/g, ' ');
        formInstance.setValue('treeModalAddEditSource', trimmedName);
        if (trimmedName === '') {
          formInstance.trigger('treeModalAddEditSource');
          return;
        }
        if (!isValid) {
          return;
        }
        selectedMenuItem ? updateSource() : addSource();
      },
      submitText: 'Save Source',
      title: `${componentVerbs.title} Source`,
      primaryButtonDisabled: !isValid
    }),
    [addSource, closeModalHandler, componentVerbs, formInstance, selectedMenuItem, updateSource, isValid]
  );

  return (
    <FormModal {...modalProps}>
      <div>
        <Input
          formInstance={formInstance}
          name={'treeModalAddEditSource'}
          rules={{
            minLength: 1,
            required: ValidationMessages.name.required,
            validate: value =>
              value.trim().replace(/\s+/g, ' ').length <= 191 || ValidationMessages.discover.source.name.maxLength
          }}
          size={'medium'}
          label={`${componentVerbs.label} Source Name`}
          value={selectedMenuItem?.name}
        />
      </div>
    </FormModal>
  );
};
